/**
 * InkingOverlay: 2D canvas overlay that renders baked CPU inking strokes.
 *
 * This is a DOM element positioned over the 3D viewport (same pattern as
 * CameraFrameOverlay). It draws directly to a Canvas 2D context in screen
 * space — no projection issues.
 *
 * Mounted as a sibling of Viewport3D in App.tsx, NOT inside the R3F Canvas.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import * as THREE from "three/webgpu";
import { useStore } from "../../app/store.ts";
import { showToast } from "../../ui/toastBus.ts";
import {
  subscribeCameraGestureStart,
  subscribeCameraMotion,
} from "../cameraSync.ts";
import { getInkingUniforms } from "../inkingUniforms.ts";
import {
  usePresenceTransition,
  VIEWPORT_MODE_TRANSITION_MS,
} from "../usePresenceTransition.ts";
import { InkingEditLayer } from "./canvas/InkingEditLayer.tsx";
import { extractFeatures, thinFeatureRidges } from "./cpu/featureExtraction.ts";
import { recycleGBuffer } from "./cpu/gbufferPool.ts";
import {
  disposeReadbackTargets,
  readbackGBuffer,
} from "./cpu/gbufferReadback.ts";
import {
  InkingBakeCancelledError,
  runInkingPipelineInWorker,
} from "./cpu/inkingWorkerClient.ts";
import {
  detectCornerMask,
  getStrokeSmoothingProfile,
  polylineBboxDiag,
} from "./cpu/strokeSmooth.ts";
import type {
  FeatureMap,
  GBufferData,
  InkingPipelineParams,
  InkingPipelineResult,
  StyledStroke,
} from "./cpu/types.ts";
import { FeatureType, SilhouetteKind } from "./cpu/types.ts";

type ContourDebugMode = "off" | "overlay" | "raw";
const INKING_DEBUG_FLAGS = {
  strokeColors: false,
  contourMode: "off" as ContourDebugMode,
};

const OVERLAY_FADE_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

/**
 * Gather pipeline params from the Zustand store state.
 */
function gatherParams(): InkingPipelineParams {
  const s = useStore.getState();
  const im = s.inkingMode;
  const u = getInkingUniforms();

  return {
    features: im?.features ?? {
      silhouette: true,
      crease: true,
      screenEdges: true,
    },
    creaseAngle: im?.creaseAngle ?? 30,
    screenEdgeDetail: s.inkingDetail,
    simplificationTolerance: im?.simplificationTolerance ?? 1.5,
    cornerAngle: im?.cornerAngle ?? 60,
    minChainLength: 24,
    baseThickness: s.inkingThickness,
    thicknessMin: s.inkingThicknessMin,
    shadowResponse: s.inkingShadowResponse,
    shadowExposure: s.inkingShadowExposure,
    silhouetteBoost: s.inkingLineWidthVariation,
    distanceNear: s.inkingDistanceNear,
    distanceFar: s.inkingDistanceFar,
    taperStrength: im?.taperStrength ?? 0.4,
    baseOpacity: s.inkingOpacity,
    inkColor: hexToRgb(s.inkingColor),
    lightRangeMin: u.lightRangeMin.value,
    lightRangeMax: u.lightRangeMax.value,
    cameraNear: u.cameraNear.value,
    cameraFar: u.cameraFar.value,
  };
}

function hexToRgb(hex: string): [number, number, number] {
  const c = new THREE.Color(hex);
  return [c.r, c.g, c.b];
}

const DEBUG_STROKE_FEATURE_COLORS: ReadonlyArray<[number, number, number]> = [
  [0.9, 0.25, 0.25],
  [0.2, 0.72, 0.28],
  [0.25, 0.45, 0.95],
];

function getStrokeColor(
  featureMix: [number, number, number],
  confidence: number,
  inkColor: [number, number, number],
  debugStrokeColors: boolean,
): [number, number, number] {
  if (!debugStrokeColors) return inkColor;

  const total = featureMix[0] + featureMix[1] + featureMix[2];
  if (total <= 1e-6) return inkColor;

  let r = 0;
  let g = 0;
  let b = 0;
  for (let i = 0; i < DEBUG_STROKE_FEATURE_COLORS.length; i++) {
    const weight = featureMix[i] / total;
    r += DEBUG_STROKE_FEATURE_COLORS[i][0] * weight;
    g += DEBUG_STROKE_FEATURE_COLORS[i][1] * weight;
    b += DEBUG_STROKE_FEATURE_COLORS[i][2] * weight;
  }

  const gain = 0.55 + Math.max(0, Math.min(1, confidence)) * 0.45;
  return [Math.min(r * gain, 1), Math.min(g * gain, 1), Math.min(b * gain, 1)];
}

interface PreviewRasterMapping {
  fullWidth: number;
  fullHeight: number;
  sourceFullWidth: number;
  sourceFullHeight: number;
  sourceOffsetX: number;
  sourceOffsetY: number;
  cropX: number;
  cropY: number;
}

interface BakedHatchingSettings {
  enabled: boolean;
  directionDeg: number;
  exposure: number;
  density: number;
  jitter: number;
}

interface HatchFieldSample {
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  shade: number;
  alpha: number;
  objectId: number;
  contourStrength: number;
}

function measureInkingFrameBox(
  wrapper: HTMLElement,
): { left: number; top: number; width: number; height: number } | null {
  const frame = document.querySelector<HTMLElement>("[data-camera-frame]");
  if (!frame) return null;

  const wrapperRect = wrapper.getBoundingClientRect();
  const frameRect = frame.getBoundingClientRect();
  return {
    left: frameRect.left - wrapperRect.left,
    top: frameRect.top - wrapperRect.top,
    width: frameRect.width,
    height: frameRect.height,
  };
}

function smoothFloatArray(arr: Float32Array, radius: number): void {
  smooth1DMode(arr, arr.length, radius, false);
}

function getPreviewRasterMapping(
  wrapper: HTMLElement,
  frameBox: { left: number; top: number; width: number; height: number },
  targetWidth: number,
  targetHeight: number,
): PreviewRasterMapping | null {
  const wrapperRect = wrapper.getBoundingClientRect();
  if (
    !Number.isFinite(frameBox.width) ||
    !Number.isFinite(frameBox.height) ||
    frameBox.width < 2 ||
    frameBox.height < 2
  ) {
    return null;
  }

  const targetAspect = targetWidth / targetHeight;
  const cssFrameW = frameBox.width;
  const cssFrameH = cssFrameW / targetAspect;
  const cssCx = frameBox.left + frameBox.width * 0.5;
  const cssCy = frameBox.top + frameBox.height * 0.5;
  const cssLeft = cssCx - cssFrameW * 0.5;
  const cssTop = cssCy - cssFrameH * 0.5;
  const scale = targetWidth / cssFrameW;
  if (!Number.isFinite(scale) || scale <= 0) {
    return null;
  }

  return {
    fullWidth: targetWidth,
    fullHeight: targetHeight,
    sourceFullWidth: Math.max(1, Math.round(wrapperRect.width * scale)),
    sourceFullHeight: Math.max(1, Math.round(wrapperRect.height * scale)),
    sourceOffsetX: Math.round(cssLeft * scale),
    sourceOffsetY: Math.round(cssTop * scale),
    cropX: 0,
    cropY: 0,
  };
}

function createCaptureCamera(
  source: THREE.Camera,
  mapping: PreviewRasterMapping,
): THREE.Camera {
  const capture = source.clone() as THREE.Camera & {
    clearViewOffset?: () => void;
    setViewOffset?: (
      fullWidth: number,
      fullHeight: number,
      x: number,
      y: number,
      width: number,
      height: number,
    ) => void;
    updateProjectionMatrix?: () => void;
  };

  capture.clearViewOffset?.();
  if (typeof capture.setViewOffset === "function") {
    capture.setViewOffset(
      mapping.sourceFullWidth,
      mapping.sourceFullHeight,
      mapping.sourceOffsetX,
      mapping.sourceOffsetY,
      mapping.fullWidth,
      mapping.fullHeight,
    );
    capture.updateProjectionMatrix?.();
  }
  capture.updateMatrixWorld();
  return capture;
}

// ---------------------------------------------------------------------------
// Bake trigger — exposed globally for the panel button
// ---------------------------------------------------------------------------

let _bakeFn: (() => void) | null = null;
let _exportFn: (() => Promise<void>) | null = null;
let _cancelFn: (() => void) | null = null;

export function triggerInkingBake(): void {
  _bakeFn?.();
}

export function cancelInkingBake(): void {
  _cancelFn?.();
}

export async function triggerInkingExport(): Promise<void> {
  await _exportFn?.();
}

// ---------------------------------------------------------------------------
// Canvas 2D stroke rendering
// ---------------------------------------------------------------------------

function dilateMask(
  mask: Uint8Array,
  radius: number,
  closed: boolean,
): Uint8Array {
  if (radius < 1) return mask;
  const out = new Uint8Array(mask);
  const n = mask.length;
  for (let i = 0; i < n; i++) {
    if (mask[i] !== 1) continue;
    for (let k = 1; k <= radius; k++) {
      if (closed) {
        out[(i + k) % n] = 1;
        out[(i + n - k) % n] = 1;
      } else {
        if (i + k < n) out[i + k] = 1;
        if (i - k >= 0) out[i - k] = 1;
      }
    }
  }
  return out;
}

function smooth1DMode(
  arr: Float32Array,
  n: number,
  radius: number,
  closed: boolean,
  preserveMask?: Uint8Array,
): void {
  if (n < 3 || radius < 1) return;
  const tmp = new Float32Array(n);
  if (!closed) {
    tmp[0] = arr[0];
    tmp[n - 1] = arr[n - 1];
  }
  const start = closed ? 0 : 1;
  const end = closed ? n : n - 1;
  for (let i = start; i < end; i++) {
    if (preserveMask?.[i] === 1) {
      tmp[i] = arr[i];
      continue;
    }
    let sum = 0;
    let count = 0;
    for (let k = -radius; k <= radius; k++) {
      const j = closed
        ? (((i + k) % n) + n) % n
        : Math.max(0, Math.min(n - 1, i + k));
      sum += arr[j];
      count++;
    }
    tmp[i] = sum / count;
  }
  arr.set(tmp);
}

function smoothBoundaryMode(
  x: Float32Array,
  y: Float32Array,
  n: number,
  radius: number,
  closed: boolean,
  preserveMask?: Uint8Array,
): void {
  smooth1DMode(x, n, radius, closed, preserveMask);
  smooth1DMode(y, n, radius, closed, preserveMask);
}

/**
 * Draw styled strokes to a Canvas 2D context.
 * Each stroke is drawn as a series of Bézier curves with variable width.
 */
function drawStrokes(
  ctx: CanvasRenderingContext2D,
  strokes: StyledStroke[],
  inkColor: [number, number, number],
  debugStrokeColors: boolean,
  canvasWidth: number,
  canvasHeight: number,
  preserveExistingPixels: boolean = false,
): void {
  if (!preserveExistingPixels) ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  // Each stroke is rendered as a filled polygon (triangle strip equivalent):
  // for every sample we compute the tangent, then offset perpendicular by
  // ±thickness along the normal to produce left/right boundary points.
  // The polygon is [L0..Ln, Rn..R0] drawn with ctx.fill(). This gives true
  // inner-stroke width dynamics: tapered endpoints, shade-driven thickening,
  // and no rounded-cap beading.
  for (const stroke of strokes) {
    const { style, samples } = stroke;
    const n = samples.length / 2;
    if (n < 2) continue;
    const closed = stroke.curve.closed;
    const smoothingProfile = getStrokeSmoothingProfile(
      stroke.curve.featureType,
      stroke.curve.resolvedAutoSmoothAngle,
      polylineBboxDiag(samples),
    );
    const cornerMask = detectCornerMask(
      samples,
      smoothingProfile.cornerAngleDeg,
      closed,
    );
    const protectedCorners = dilateMask(
      cornerMask,
      smoothingProfile.cornerProtectionRadius,
      closed,
    );

    // Skip strokes whose average styling is below visibility thresholds.
    let sumT = 0;
    let sumA = 0;
    for (let i = 0; i < n; i++) {
      sumT += style.thickness[i];
      sumA += style.opacity[i];
    }
    const meanT = sumT / n;
    const meanA = sumA / n;
    if (meanT < 0.3 || meanA < 0.02) continue;

    // Build the left and right boundary arrays.
    const leftX = new Float32Array(n);
    const leftY = new Float32Array(n);
    const rightX = new Float32Array(n);
    const rightY = new Float32Array(n);

    // Smooth the thickness array to remove per-sample oscillation.
    // This is the key fix: even small thickness jitter at each sample
    // point gets amplified by the perpendicular offset into visible
    // boundary oscillation. Smoothing the thickness first produces
    // consistently smooth boundary geometry.
    const smoothedT = new Float32Array(style.thickness);
    smooth1DMode(
      smoothedT,
      n,
      smoothingProfile.thicknessSmoothingRadius,
      closed,
      protectedCorners,
    );

    // Compute tangent and perpendicular offset for each sample.
    // Uses a wide central-difference window for stable direction.
    for (let i = 0; i < n; i++) {
      const px = samples[i * 2];
      const py = samples[i * 2 + 1];
      const t = smoothedT[i];

      const prev = closed ? (i + n - 1) % n : Math.max(0, i - 1);
      const next = closed ? (i + 1) % n : Math.min(n - 1, i + 1);
      if (cornerMask[i] === 1 && (closed || (i > 0 && i < n - 1))) {
        const ax = px - samples[prev * 2];
        const ay = py - samples[prev * 2 + 1];
        const bx = samples[next * 2] - px;
        const by = samples[next * 2 + 1] - py;
        const lenA = Math.hypot(ax, ay);
        const lenB = Math.hypot(bx, by);

        if (lenA > 1e-6 && lenB > 1e-6) {
          const n0x = -ay / lenA;
          const n0y = ax / lenA;
          const n1x = -by / lenB;
          const n1y = bx / lenB;
          const mx = n0x + n1x;
          const my = n0y + n1y;
          const mLen = Math.hypot(mx, my);

          if (mLen > 1e-6) {
            const miterX = mx / mLen;
            const miterY = my / mLen;
            const denom = miterX * n1x + miterY * n1y;
            if (denom > 0.2) {
              const miterLen = Math.min(t / denom, t * 3.0);
              leftX[i] = px + miterX * miterLen;
              leftY[i] = py + miterY * miterLen;
              rightX[i] = px - miterX * miterLen;
              rightY[i] = py - miterY * miterLen;
              continue;
            }
          }
        }
      }

      // Tangent: widest possible window clamped to array bounds
      let tx: number;
      let ty: number;
      if (closed && n >= 3) {
        tx = samples[next * 2] - samples[prev * 2];
        ty = samples[next * 2 + 1] - samples[prev * 2 + 1];
      } else {
        const wideWindow = protectedCorners[i] === 1 ? 1 : 3;
        const w = Math.min(wideWindow, i, n - 1 - i);
        if (w === 0) {
          const other = i === 0 ? 1 : n - 2;
          tx = samples[other * 2] - px;
          ty = samples[other * 2 + 1] - py;
        } else {
          tx = samples[(i + w) * 2] - samples[(i - w) * 2];
          ty = samples[(i + w) * 2 + 1] - samples[(i - w) * 2 + 1];
        }
      }
      const tlen = Math.sqrt(tx * tx + ty * ty);
      if (tlen < 1e-6) {
        leftX[i] = px;
        leftY[i] = py;
        rightX[i] = px;
        rightY[i] = py;
        continue;
      }
      const nx = -ty / tlen;
      const ny = tx / tlen;

      leftX[i] = px + nx * t;
      leftY[i] = py + ny * t;
      rightX[i] = px - nx * t;
      rightY[i] = py - ny * t;
    }

    // Smooth the boundary arrays — two passes of radius=2 is equivalent to
    // a single wider kernel (~radius 3.5) but preserves more local shape
    // than a single wide pass.
    const boundaryRadius = smoothingProfile.boundarySmoothingRadius;
    smoothBoundaryMode(
      leftX,
      leftY,
      n,
      boundaryRadius,
      closed,
      protectedCorners,
    );
    smoothBoundaryMode(
      rightX,
      rightY,
      n,
      boundaryRadius,
      closed,
      protectedCorners,
    );
    smoothBoundaryMode(
      leftX,
      leftY,
      n,
      boundaryRadius,
      closed,
      protectedCorners,
    );
    smoothBoundaryMode(
      rightX,
      rightY,
      n,
      boundaryRadius,
      closed,
      protectedCorners,
    );

    ctx.beginPath();
    ctx.moveTo(leftX[0], leftY[0]);
    for (let i = 1; i < n; i++) ctx.lineTo(leftX[i], leftY[i]);
    for (let i = n - 1; i >= 0; i--) ctx.lineTo(rightX[i], rightY[i]);
    ctx.closePath();

    const [cr, cg, cb] = getStrokeColor(
      stroke.curve.featureMix,
      stroke.curve.confidence,
      inkColor,
      debugStrokeColors,
    );
    const r = Math.round(cr * 255);
    const g = Math.round(cg * 255);
    const b = Math.round(cb * 255);

    // Fill uses the mean opacity (per-sample alpha in a filled polygon
    // requires a gradient or separate sub-polygons; mean opacity is
    // visually close and inexpensive). Thickness variation is already
    // fully preserved by the polygon geometry.
    ctx.fillStyle = `rgba(${r},${g},${b},${meanA})`;
    ctx.fill();
  }
}

function drawCroppedStrokes(
  ctx: CanvasRenderingContext2D,
  strokes: StyledStroke[],
  inkColor: [number, number, number],
  debugStrokeColors: boolean,
  canvasWidth: number,
  canvasHeight: number,
  cropX: number,
  cropY: number,
  preserveExistingPixels: boolean = false,
): void {
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  if (!preserveExistingPixels) ctx.clearRect(0, 0, canvasWidth, canvasHeight);
  ctx.translate(-cropX, -cropY);
  drawStrokes(
    ctx,
    strokes,
    inkColor,
    debugStrokeColors,
    canvasWidth,
    canvasHeight,
    true,
  );
  ctx.restore();
}

function drawHatchStrokes(
  ctx: CanvasRenderingContext2D,
  strokes: StyledStroke[],
  inkColor: [number, number, number],
  canvasWidth: number,
  canvasHeight: number,
  preserveExistingPixels: boolean = false,
): void {
  if (!preserveExistingPixels) ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  const r = Math.round(inkColor[0] * 255);
  const g = Math.round(inkColor[1] * 255);
  const b = Math.round(inkColor[2] * 255);

  for (const stroke of strokes) {
    const { samples, style } = stroke;
    const n = samples.length / 2;
    if (n < 2) continue;

    for (let i = 0; i < n - 1; i++) {
      const x0 = samples[i * 2];
      const y0 = samples[i * 2 + 1];
      const x1 = samples[(i + 1) * 2];
      const y1 = samples[(i + 1) * 2 + 1];
      const dx = x1 - x0;
      const dy = y1 - y0;
      const segLen = Math.hypot(dx, dy);
      if (segLen < 1e-4) continue;

      const opacity = clamp01((style.opacity[i] + style.opacity[i + 1]) * 0.5);
      const t0 = Math.max(0, style.thickness[i]);
      const t1 = Math.max(0, style.thickness[i + 1]);
      if (opacity < 0.004 || Math.max(t0, t1) < 0.01) continue;

      const nx = -dy / segLen;
      const ny = dx / segLen;
      ctx.beginPath();
      ctx.moveTo(x0 + nx * t0, y0 + ny * t0);
      ctx.lineTo(x1 + nx * t1, y1 + ny * t1);
      ctx.lineTo(x1 - nx * t1, y1 - ny * t1);
      ctx.lineTo(x0 - nx * t0, y0 - ny * t0);
      ctx.closePath();
      ctx.fillStyle = `rgba(${r},${g},${b},${opacity})`;
      ctx.fill();
    }
  }
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function smoothstep(lo: number, hi: number, x: number): number {
  if (hi <= lo) return x < lo ? 0 : 1;
  const t = clamp01((x - lo) / (hi - lo));
  return t * t * (3 - 2 * t);
}

function mix(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function signedNoise2D(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453123;
  return (n - Math.floor(n)) * 2 - 1;
}

function computeShadeFromLuminance(
  luminance: number,
  lightRangeMin: number,
  lightRangeMax: number,
  bias: number,
): number {
  const lightMax = Math.max(lightRangeMax, lightRangeMin + 0.08);
  const lightT = clamp01(
    (luminance - lightRangeMin) / (lightMax - lightRangeMin + 1e-6),
  );
  const biasOffset = mix(-0.28, 0.28, bias);
  const visibleLight = smoothstep(0.04, 0.96, clamp01(lightT + biasOffset));
  return 1 - visibleLight;
}

function buildContourHatchingStrokes(
  gbuffer: GBufferData,
  params: InkingPipelineParams,
  settings: BakedHatchingSettings,
  cropX: number,
  cropY: number,
  canvasWidth: number,
  canvasHeight: number,
): StyledStroke[] {
  if (!settings.enabled) return [];

  const normalizedDensity = clamp01(settings.density);
  const densityBoost =
    settings.density > 1
      ? mix(1, 1.25, clamp01((settings.density - 1) / 0.25))
      : 1;
  const spacing = Math.round(mix(48, 14, normalizedDensity) / densityBoost);
  const stepLength = Math.max(1.2, spacing * 0.18);
  const directionAngle = settings.directionDeg * (Math.PI / 180);
  const dirX = Math.cos(directionAngle);
  const dirY = Math.sin(directionAngle);
  const normalX = -dirY;
  const normalY = dirX;
  const { width, height, depth, luminance, normals, objectIds } = gbuffer;
  const strokes: StyledStroke[] = [];

  const centerX = (canvasWidth - 1) * 0.5;
  const centerY = (canvasHeight - 1) * 0.5;
  const cornerOffsets = [
    -centerX * normalX + -centerY * normalY,
    (canvasWidth - 1 - centerX) * normalX + -centerY * normalY,
    -centerX * normalX + (canvasHeight - 1 - centerY) * normalY,
    (canvasWidth - 1 - centerX) * normalX +
      (canvasHeight - 1 - centerY) * normalY,
  ];
  const minOffset = Math.min(...cornerOffsets) - spacing;
  const maxOffset = Math.max(...cornerOffsets) + spacing;
  const offsetStart = Math.floor(minOffset / spacing);
  const offsetEnd = Math.ceil(maxOffset / spacing);
  // Hatching peaks at silhouette thickness; shadowResponse adds extra bite
  // in deep shadow. Per-sample values below scale with shade so a single
  // line thickens and fades along a lighting gradient.
  const hatchPeakThickness = params.baseThickness;
  const hatchShadowBoost = params.shadowResponse * 0.5;

  const clipInfiniteLineToRect = (
    originX: number,
    originY: number,
  ): { startX: number; startY: number; endX: number; endY: number } | null => {
    const candidates: Array<{ t: number; x: number; y: number }> = [];
    const xMin = 0;
    const xMax = canvasWidth - 1;
    const yMin = 0;
    const yMax = canvasHeight - 1;

    const pushCandidate = (t: number) => {
      const x = originX + dirX * t;
      const y = originY + dirY * t;
      if (
        x < xMin - 1e-4 ||
        x > xMax + 1e-4 ||
        y < yMin - 1e-4 ||
        y > yMax + 1e-4
      )
        return;
      for (const existing of candidates) {
        if (Math.abs(existing.t - t) < 1e-4) return;
      }
      candidates.push({ t, x, y });
    };

    if (Math.abs(dirX) > 1e-6) {
      pushCandidate((xMin - originX) / dirX);
      pushCandidate((xMax - originX) / dirX);
    }
    if (Math.abs(dirY) > 1e-6) {
      pushCandidate((yMin - originY) / dirY);
      pushCandidate((yMax - originY) / dirY);
    }

    if (candidates.length < 2) return null;
    candidates.sort((a, b) => a.t - b.t);
    const start = candidates[0];
    const end = candidates[candidates.length - 1];
    if (Math.hypot(end.x - start.x, end.y - start.y) < spacing * 0.75)
      return null;
    return { startX: start.x, startY: start.y, endX: end.x, endY: end.y };
  };

  const finalizeSpan = (points: HatchFieldSample[], lineLength: number) => {
    if (points.length < 3 || lineLength < spacing * 0.9) return;

    const rawSamples = new Float32Array(points.length * 2);
    const thickness = new Float32Array(points.length);
    const opacity = new Float32Array(points.length);
    let shadeSum = 0;
    let maxShade = 0;

    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      // shade^1.35 is a concave curve biased toward shadows — midtones
      // compress, shadows keep their response.
      const shadeStrength = point.shade ** 1.35;
      rawSamples[i * 2] = point.x - cropX;
      rawSamples[i * 2 + 1] = point.y - cropY;
      thickness[i] =
        hatchPeakThickness *
        shadeStrength *
        (1 + hatchShadowBoost * shadeStrength);
      opacity[i] = params.baseOpacity * shadeStrength;
      shadeSum += point.shade;
      if (point.shade > maxShade) maxShade = point.shade;
    }

    smoothFloatArray(thickness, 5);
    smoothFloatArray(opacity, 6);

    if (maxShade < 0.12) return;

    strokes.push({
      curve: {
        segments: [],
        featureType: FeatureType.Crease,
        confidence: shadeSum / points.length,
        featureMix: [0, 1, 0],
        closed: false,
        resolvedAutoSmoothAngle: 180,
      },
      style: { thickness, opacity },
      samples: rawSamples,
    });
  };

  for (let lineIndex = offsetStart; lineIndex <= offsetEnd; lineIndex++) {
    const offsetJitter = settings.jitter * spacing * 0.35;
    const offset =
      lineIndex * spacing +
      signedNoise2D(lineIndex * 13.7 + cropX * 0.1, cropY * 0.11 + 19.3) *
        offsetJitter;
    const originX = centerX + normalX * offset;
    const originY = centerY + normalY * offset;
    const clipped = clipInfiniteLineToRect(originX, originY);
    if (!clipped) continue;

    const totalLength = Math.hypot(
      clipped.endX - clipped.startX,
      clipped.endY - clipped.startY,
    );
    const sampleCount = Math.max(2, Math.floor(totalLength / stepLength) + 1);
    let currentObjectId = 0;
    let currentPoints: HatchFieldSample[] = [];
    let currentLength = 0;
    let prevX = 0;
    let prevY = 0;

    const flush = () => {
      finalizeSpan(currentPoints, currentLength);
      currentPoints = [];
      currentLength = 0;
      currentObjectId = 0;
    };

    for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex++) {
      const t = sampleCount === 1 ? 0 : sampleIndex / (sampleCount - 1);
      const x = mix(clipped.startX, clipped.endX, t);
      const y = mix(clipped.startY, clipped.endY, t);
      const sx = Math.round(x + cropX);
      const sy = Math.round(y + cropY);
      if (sx < 0 || sy < 0 || sx >= width || sy >= height) {
        flush();
        continue;
      }

      const idx = sy * width + sx;
      const objectId = objectIds[idx];
      if (objectId === 0 || depth[idx] <= 0) {
        flush();
        continue;
      }

      if (currentObjectId !== 0 && objectId !== currentObjectId) {
        flush();
      }
      if (currentObjectId === 0) currentObjectId = objectId;

      const shade = computeShadeFromLuminance(
        luminance[idx],
        params.lightRangeMin,
        params.lightRangeMax,
        settings.exposure,
      );
      const ni = idx * 3;
      const contourStrength = Math.hypot(normals[ni], normals[ni + 1]);
      if (currentPoints.length > 0) {
        currentLength += Math.hypot(x - prevX, y - prevY);
      }
      currentPoints.push({
        x: x + cropX,
        y: y + cropY,
        dirX,
        dirY,
        shade,
        alpha: 0,
        objectId,
        contourStrength,
      });
      prevX = x;
      prevY = y;
    }

    flush();
  }

  return strokes;
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Debug: draw the raw feature map to the canvas so we can see what the
// detectors produce before stroke construction.
// ---------------------------------------------------------------------------

function drawFeatureMapDebug(
  canvas: HTMLCanvasElement | null,
  featureMap: FeatureMap,
  w: number,
  h: number,
  cropX: number = 0,
  cropY: number = 0,
  outW: number = w,
  outH: number = h,
): void {
  if (!canvas) return;
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const imgData = ctx.createImageData(outW, outH);
  const pixels = imgData.data;

  // Color-code:
  // - background silhouette = red
  // - inter-object boundary = orange
  // - self-occlusion promotion = magenta
  // - crease = green
  // - screen edge = blue
  for (let y = 0; y < outH; y++) {
    const srcY = y + cropY;
    if (srcY < 0 || srcY >= h) continue;
    for (let x = 0; x < outW; x++) {
      const srcX = x + cropX;
      if (srcX < 0 || srcX >= w) continue;
      const srcI = srcY * w + srcX;
      const conf = featureMap.confidence[srcI];
      if (conf < 0.15) continue;
      const ft = featureMap.featureType[srcI];
      const pi = (y * outW + x) * 4;
      const intensity = Math.round(conf * 255);
      if (ft === FeatureType.Silhouette) {
        const kind = featureMap.silhouetteKind[srcI] as SilhouetteKind;
        if (kind === SilhouetteKind.ObjectBoundary) {
          pixels[pi] = intensity;
          pixels[pi + 1] = Math.round(intensity * 0.55);
          pixels[pi + 2] = 35;
        } else if (kind === SilhouetteKind.SelfOcclusion) {
          pixels[pi] = Math.round(intensity * 0.9);
          pixels[pi + 1] = 35;
          pixels[pi + 2] = intensity;
        } else {
          pixels[pi] = intensity;
          pixels[pi + 1] = 30;
          pixels[pi + 2] = 30;
        }
      } else if (ft === FeatureType.Crease) {
        pixels[pi] = 30;
        pixels[pi + 1] = intensity; // G
        pixels[pi + 2] = 30;
      } else {
        pixels[pi] = 30;
        pixels[pi + 1] = 30;
        pixels[pi + 2] = intensity; // B
      }
      pixels[pi + 3] = 200;
    }
  }

  ctx.putImageData(imgData, 0, 0);
  console.log(
    `[Inking:debug] Feature map drawn: ${outW}×${outH} from ${w}×${h}`,
  );
}

// ---------------------------------------------------------------------------
// InkingOverlay component (DOM-based, outside R3F Canvas)
// ---------------------------------------------------------------------------

export function InkingOverlay() {
  const workspaceMode = useStore((s) => s.workspaceMode);
  const inkingMode = useStore((s) => s.inkingMode);
  const renderWidth = useStore((s) => s.renderWidth);
  const renderHeight = useStore((s) => s.renderHeight);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const resultRef = useRef<InkingPipelineResult | null>(null);
  const hatchStrokesRef = useRef<StyledStroke[] | null>(null);
  const bakeParamsRef = useRef<InkingPipelineParams | null>(null);
  const hatchingSettingsRef = useRef<BakedHatchingSettings | null>(null);
  const rasterMappingRef = useRef<PreviewRasterMapping | null>(null);
  const [frameBox, setFrameBox] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  const isActive = workspaceMode === "inking" && inkingMode != null;
  const { shouldRender, isVisible } = usePresenceTransition(isActive);

  const autoGenerate = useStore((s) => s.inkingAutoGenerate);
  // True iff no camera motion event has fired in the last SETTLE_MS. This is
  // the single source of truth for the auto-bake state machine: opacity, the
  // motion-start canvas wipe, and the bake-driver gate all key off it.
  const [cameraSettled, setCameraSettled] = useState(true);

  useLayoutEffect(() => {
    if (!shouldRender) {
      setFrameBox(null);
      return;
    }

    const measure = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        setFrameBox(null);
        return;
      }
      setFrameBox(measureInkingFrameBox(wrapper));
    };

    measure();
    const wrapper = wrapperRef.current;
    const frame = document.querySelector<HTMLElement>("[data-camera-frame]");
    const observer = new ResizeObserver(measure);
    if (wrapper) observer.observe(wrapper);
    if (frame) observer.observe(frame);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [shouldRender]);

  const clearBakedPreview = useCallback(
    (resetBakedFlag: boolean) => {
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = renderWidth;
        canvas.height = renderHeight;
      }
      resultRef.current = null;
      hatchStrokesRef.current = null;
      bakeParamsRef.current = null;
      hatchingSettingsRef.current = null;
      rasterMappingRef.current = null;
      if (resetBakedFlag) {
        const current = useStore.getState().inkingMode;
        if (current) {
          // Clearing the baked preview also invalidates the editable raster —
          // the user can no longer "edit" strokes that are about to be regenerated.
          useStore.setState({
            inkingMode: {
              ...current,
              baked: false,
              rasterBuffer: null,
              rasterWidth: 0,
              rasterHeight: 0,
              rasterSeq: current.rasterSeq + 1,
              rasterDirtyFromBake: false,
            },
          });
        }
      }
    },
    [renderWidth, renderHeight],
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  const bake = useCallback(async () => {
    const s = useStore.getState();
    if (!s.inkingMode) return;

    // Supersede any in-flight bake. The previous controller is aborted; the
    // `isCurrent()` guard below ensures its async tail (catch handler,
    // progress messages) cannot mutate state once we install our controller.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const isCurrent = () => abortControllerRef.current === controller;

    useStore.setState({
      inkingMode: {
        ...s.inkingMode,
        baking: true,
        bakeProgress: 0,
        bakePhase: "init",
      },
    });
    console.log("[Inking] Starting bake...");

    const finalizeBaking = (patch: { baked?: boolean }) => {
      if (!isCurrent()) return;
      const current = useStore.getState().inkingMode;
      if (!current) return;
      useStore.setState({
        inkingMode: {
          ...current,
          baking: false,
          bakeProgress: 0,
          bakePhase: "init",
          ...(patch.baked !== undefined ? { baked: patch.baked } : {}),
        },
      });
    };

    let gbuffer: GBufferData | null = null;
    let transferredGBuffer = false;
    try {
      const params = gatherParams();

      // Access renderer, scene, camera via globals exposed by Viewport3D
      const renderer = window.__poseref_renderer;
      const scene = window.__poseref_scene;
      const camera = window.__poseref_camera;
      if (!renderer || !scene || !camera) {
        throw new Error(
          "Viewport not ready — renderer, scene, or camera not available",
        );
      }

      const w = s.renderWidth;
      const h = s.renderHeight;
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        throw new Error("Inking preview frame is not ready");
      }
      const freshFrameBox = measureInkingFrameBox(wrapper);
      if (!freshFrameBox) {
        throw new Error("Inking preview frame is not ready");
      }
      setFrameBox(freshFrameBox);
      const rasterMapping = getPreviewRasterMapping(
        wrapper,
        freshFrameBox,
        w,
        h,
      );
      if (!rasterMapping) {
        throw new Error("Inking preview frame has invalid dimensions");
      }
      rasterMappingRef.current = rasterMapping;
      const captureCamera = createCaptureCamera(camera, rasterMapping);
      console.log(
        `[Inking] Preview/export target: ${w}×${h}, capture raster: ` +
          `${rasterMapping.fullWidth}×${rasterMapping.fullHeight}, full-frustum equivalent: ` +
          `${rasterMapping.sourceFullWidth}×${rasterMapping.sourceFullHeight}`,
      );

      // Async G-buffer readback (main thread — bound to the WebGPU renderer)
      gbuffer = await readbackGBuffer(
        renderer,
        scene,
        captureCamera,
        rasterMapping.fullWidth,
        rasterMapping.fullHeight,
      );
      if (controller.signal.aborted) {
        finalizeBaking({});
        return;
      }

      hatchingSettingsRef.current = {
        enabled: s.inkingHatchingEnabled,
        directionDeg: s.inkingHatchingDirection,
        exposure: s.inkingHatchingExposure,
        density: s.inkingHatchingDensity,
        jitter: s.inkingHatchingJitter,
      };

      const contourDebugMode = INKING_DEBUG_FLAGS.contourMode;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = w;
        canvas.height = h;
      }
      if (contourDebugMode !== "off") {
        // The debug feature-map visualisation still runs on the main thread.
        // It's only triggered when a developer sets the debug flag, so we
        // deliberately don't port it onto the worker path.
        const featureMap = thinFeatureRidges(extractFeatures(gbuffer, params));
        console.log(
          `[Inking:debug] Contour debug ${contourDebugMode} enabled: background silhouette=red, ` +
            "object boundary=orange, self-occlusion=magenta, crease=green, screen=blue",
        );
        drawFeatureMapDebug(
          canvas,
          featureMap,
          rasterMapping.fullWidth,
          rasterMapping.fullHeight,
          rasterMapping.cropX,
          rasterMapping.cropY,
          w,
          h,
        );
      }

      // The worker consumes the G-buffer typed-array buffers via Transferable,
      // so we build hatching strokes BEFORE dispatching. After the dispatch,
      // the main-thread views detach and the worker returns the buffers to the
      // reusable pool with its terminal response.
      hatchStrokesRef.current = buildContourHatchingStrokes(
        gbuffer,
        params,
        hatchingSettingsRef.current,
        rasterMapping.cropX,
        rasterMapping.cropY,
        w,
        h,
      );
      bakeParamsRef.current = params;

      const resultPromise = runInkingPipelineInWorker(gbuffer, params, {
        signal: controller.signal,
        stallWarningMs: 8000,
        stallMessage:
          "Inking pipeline is taking longer than expected. You can cancel from the panel.",
        onProgress: (ev) => {
          if (!isCurrent()) return;
          const current = useStore.getState().inkingMode;
          if (!current) return;
          useStore.setState({
            inkingMode: {
              ...current,
              bakeProgress: ev.progress,
              bakePhase: ev.phase,
            },
          });
        },
      });
      transferredGBuffer = true;
      gbuffer = null;
      const result = await resultPromise;

      if (controller.signal.aborted) {
        finalizeBaking({});
        return;
      }

      resultRef.current = result;

      const debugStrokeColors = INKING_DEBUG_FLAGS.strokeColors;
      if (debugStrokeColors) {
        console.log(
          "[Inking:debug] Stroke debug colors enabled: pure silhouette=red, " +
            "pure crease=green, pure screen=blue, mixtures blend, brightness=confidence",
        );
      }

      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          if (contourDebugMode === "raw") {
            console.log(
              "[Inking:debug] Raw contour feature map only; skipping baked stroke draw",
            );
          } else {
            const hatchStrokes = hatchStrokesRef.current ?? [];
            if (hatchStrokes.length > 0) {
              drawHatchStrokes(
                ctx,
                hatchStrokes,
                params.inkColor,
                w,
                h,
                contourDebugMode === "overlay",
              );
            }
            drawCroppedStrokes(
              ctx,
              result.strokes,
              params.inkColor,
              debugStrokeColors,
              w,
              h,
              rasterMapping.cropX,
              rasterMapping.cropY,
              contourDebugMode === "overlay" || hatchStrokes.length > 0,
            );
            console.log(
              `[Inking] Drew ${result.strokes.length} strokes to 2D canvas`,
            );

            // Commit the rendered bake into an editable raster buffer. We
            // clone out of the canvas's ImageData so the editable buffer is
            // detached from the GPU-backed canvas and safe to mutate in
            // the edit sub-mode (pen/eraser write into this same buffer,
            // then we putImageData back for display).
            try {
              const img = ctx.getImageData(0, 0, w, h);
              const buf = new Uint8ClampedArray(img.data);
              useStore.getState().setInkingRasterBuffer(buf, w, h);
            } catch (readbackErr) {
              console.warn(
                "[Inking] Failed to capture editable raster:",
                readbackErr,
              );
            }
          }
        }
      }

      finalizeBaking({ baked: true });

      const stats = result.stats;
      console.log(
        `[Inking] Baked in ${stats.elapsedMs.toFixed(0)}ms: ` +
          `${stats.featurePixels} feature px → ${stats.chainCount} chains → ` +
          `${stats.curveCount} curves (${stats.totalSegments} segments)`,
      );
    } catch (err) {
      if (err instanceof InkingBakeCancelledError) {
        console.log("[Inking] Bake cancelled by user");
        finalizeBaking({});
        return;
      }
      console.error("[Inking] Bake failed:", err);
      const isWorkerLoadError =
        err instanceof Error &&
        (err.message.includes("MIME type") ||
          err.message.includes("Worker error"));
      showToast(
        isWorkerLoadError
          ? "Inking failed — the background worker could not load. Try reloading the page."
          : "Inking failed. Check the console for details.",
        { severity: "error", duration: 8000, dedupKey: "inking-bake-error" },
      );
      finalizeBaking({});
    } finally {
      // If the worker never took ownership, release the borrowed G-buffer
      // immediately instead of keeping a detached or stale copy in the UI.
      if (gbuffer && !transferredGBuffer) {
        recycleGBuffer(gbuffer);
      }
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, []);

  const cancelBake = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const exportPng = useCallback(async () => {
    const s = useStore.getState();
    if (!isActive || s.inkingMode?.baking) return;

    const targetWidth = s.renderWidth;
    const targetHeight = s.renderHeight;

    // Prefer the editable raster when it exists at the current render size —
    // it reflects any pen/eraser edits made by the user on top of the bake.
    const rasterBuf = s.inkingMode?.rasterBuffer;
    const rasterW = s.inkingMode?.rasterWidth ?? 0;
    const rasterH = s.inkingMode?.rasterHeight ?? 0;
    if (rasterBuf && rasterW === targetWidth && rasterH === targetHeight) {
      const exportCanvas = document.createElement("canvas");
      exportCanvas.width = targetWidth;
      exportCanvas.height = targetHeight;
      const ctx = exportCanvas.getContext("2d");
      if (!ctx) return;
      const imgData = ctx.createImageData(targetWidth, targetHeight);
      imgData.data.set(rasterBuf);
      ctx.putImageData(imgData, 0, 0);
      const blob = await canvasToPngBlob(exportCanvas);
      if (!blob) return;
      downloadBlob(blob, "poseref-inking.png");
      return;
    }

    if (
      !resultRef.current ||
      !bakeParamsRef.current ||
      canvasRef.current?.width !== targetWidth ||
      canvasRef.current?.height !== targetHeight
    ) {
      await bake();
    }

    if (!resultRef.current || !bakeParamsRef.current) return;
    const rasterMapping = rasterMappingRef.current;
    const hatchStrokes = hatchStrokesRef.current;
    if (!rasterMapping || !hatchStrokes) return;

    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = targetWidth;
    exportCanvas.height = targetHeight;
    const ctx = exportCanvas.getContext("2d");
    if (!ctx) return;

    if (hatchStrokes.length > 0) {
      drawHatchStrokes(
        ctx,
        hatchStrokes,
        bakeParamsRef.current.inkColor,
        targetWidth,
        targetHeight,
        false,
      );
    }
    drawCroppedStrokes(
      ctx,
      resultRef.current.strokes,
      bakeParamsRef.current.inkColor,
      false,
      targetWidth,
      targetHeight,
      rasterMapping.cropX,
      rasterMapping.cropY,
      hatchStrokes.length > 0,
    );

    const blob = await canvasToPngBlob(exportCanvas);
    if (!blob) return;
    downloadBlob(blob, "poseref-inking.png");
  }, [bake, isActive]);

  // Register global bake function
  useEffect(() => {
    _bakeFn = bake;
    _exportFn = exportPng;
    _cancelFn = cancelBake;
    return () => {
      _bakeFn = null;
      _exportFn = null;
      _cancelFn = null;
    };
  }, [bake, exportPng, cancelBake]);

  // ---- Auto-bake state machine ------------------------------------------
  //
  // One state: `cameraSettled`. One driver: a derived gate `shouldBake`.
  //
  // Tricky bit: OrbitControls damping fires `change` events for ~1 s after
  // the user releases the mouse. We treat those as motion (the camera *is*
  // visibly moving). After damping settles, we declare cameraSettled = true
  // and fire the bake. But the bake's setState wakes R3F's render loop,
  // which calls `controls.update()` once more — and any residual
  // `sphericalDelta` left below the damping EPS produces another `change`,
  // which, if treated as motion, restarts the cycle and bakes loop forever.
  //
  // The fix is to gate motion-tracking on a real user gesture. We track an
  // `inGesture` flag that flips true on `notifyCameraGestureStart` (mouse-
  // down on OrbitControls, WASD/zoom-key first-press, fly-mode mouse look)
  // and false at settle. Change events that arrive while `inGesture` is
  // false are damping echoes and are ignored.
  //
  //   1. Gesture start → inGesture = true, cameraSettled = false. Rearm
  //      250 ms timer.
  //   2. Motion event while inGesture → rearm timer.
  //   3. Motion event while !inGesture → ignored (damping echo).
  //   4. Timer fires → inGesture = false, cameraSettled = true.
  //   5. cameraSettled flips false → wipe canvas + raster store entry.
  //   6. shouldBake flips true → fire bake. shouldBake flips false → abort.
  useEffect(() => {
    if (!isActive) {
      setCameraSettled(true);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inGesture = false;
    const armSettleTimer = () => {
      if (timer != null) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        inGesture = false;
        setCameraSettled(true);
      }, 250);
    };
    const onGestureStart = () => {
      inGesture = true;
      setCameraSettled(false);
      armSettleTimer();
    };
    const onMotion = () => {
      if (!inGesture) return;
      armSettleTimer();
    };
    const unsubStart = subscribeCameraGestureStart(onGestureStart);
    const unsubMotion = subscribeCameraMotion(onMotion);
    return () => {
      unsubStart();
      unsubMotion();
      if (timer != null) clearTimeout(timer);
    };
  }, [isActive]);

  useEffect(() => {
    if (cameraSettled) return;
    clearBakedPreview(true);
  }, [cameraSettled, clearBakedPreview]);

  const editing = inkingMode?.editSubMode === "edit";
  const shouldBake =
    isActive && autoGenerate && cameraSettled && frameBox != null && !editing;
  useEffect(() => {
    if (shouldBake) {
      _bakeFn?.();
    } else {
      abortControllerRef.current?.abort();
    }
  }, [shouldBake]);

  // Clear canvas when leaving inking mode
  useEffect(() => {
    if (!isActive) {
      // Cancel any in-flight bake so the worker drops its work rather than
      // posting stale strokes back after the user has moved on.
      abortControllerRef.current?.abort();
      disposeReadbackTargets();
    }
  }, [isActive]);

  useEffect(() => {
    if (!shouldRender && !isActive) {
      clearBakedPreview(false);
    }
  }, [shouldRender, isActive, clearBakedPreview]);

  // Sync the view-mode preview canvas with the editable raster. This is the
  // single source of visual truth after a bake — it starts as the direct
  // stroke render and absorbs every pen/eraser edit the user commits in
  // edit sub-mode, so exiting edit always shows the refined result and
  // `exportPng` exports exactly what the user sees.
  const rasterBufferState = inkingMode?.rasterBuffer ?? null;
  const rasterBufW = inkingMode?.rasterWidth ?? 0;
  const rasterBufH = inkingMode?.rasterHeight ?? 0;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!shouldRender || !canvas) return;
    if (!rasterBufferState || rasterBufW === 0 || rasterBufH === 0) return;
    if (canvas.width !== rasterBufW) canvas.width = rasterBufW;
    if (canvas.height !== rasterBufH) canvas.height = rasterBufH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const imgData = ctx.createImageData(rasterBufW, rasterBufH);
    imgData.data.set(rasterBufferState);
    ctx.putImageData(imgData, 0, 0);
  }, [shouldRender, rasterBufferState, rasterBufW, rasterBufH]);

  // Edit sub-mode dims the 3D render so the page reads as opaque paper;
  // enabling "Show reference" drops the dim back to the view-mode level for
  // alignment touch-ups.
  const showReference = inkingMode?.showReference ?? false;
  const backdropAlpha = editing && !showReference ? 0.98 : 0.72;
  const canDelegateToEditLayer =
    editing && (inkingMode?.rasterBuffer ?? null) !== null;

  // While the camera is unsettled, fade the backdrop so the user can see the
  // live 3D scene through the overlay. The canvas itself is wiped on motion
  // start (see the cameraSettled effect above), so this fade only carries
  // the white veil — strokes are already gone, not lingering as onion skin.
  const contentOpacity = cameraSettled ? 1 : 0.15;

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "absolute",
        inset: 0,
        // Edit sub-mode captures pointer events inside the edit layer itself;
        // the wrapper remains pointer-transparent so viewport gestures pass
        // through to the 3D canvas in view sub-mode.
        pointerEvents: "none",
        zIndex: 3,
        display: shouldRender ? "block" : "none",
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${VIEWPORT_MODE_TRANSITION_MS}ms ${OVERLAY_FADE_EASING}`,
        willChange: "opacity",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: contentOpacity,
          transition: `opacity 500ms ${OVERLAY_FADE_EASING}`,
          willChange: "opacity",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `rgba(255, 255, 255, ${backdropAlpha})`,
            transition: `background ${VIEWPORT_MODE_TRANSITION_MS}ms ${OVERLAY_FADE_EASING}`,
          }}
        />
        {frameBox && (
          <canvas
            ref={canvasRef}
            width={renderWidth}
            height={renderHeight}
            style={{
              position: "absolute",
              left: frameBox.left,
              top: frameBox.top,
              width: frameBox.width,
              height: frameBox.height,
              background: "transparent",
              borderRadius: 2,
              // When the edit layer is mounted it renders the same raster with
              // pan/zoom applied, so hide the view-mode preview to avoid a
              // ghosted double-draw at 1:1 while the stage is scaled.
              visibility: canDelegateToEditLayer ? "hidden" : "visible",
            }}
          />
        )}
        {frameBox && canDelegateToEditLayer && (
          <InkingEditLayer
            frameBox={frameBox}
            renderWidth={renderWidth}
            renderHeight={renderHeight}
          />
        )}
      </div>
    </div>
  );
}
